import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, X } from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 25;
const ANY = "__any__";

const CHECKIN_TYPE_BADGE = {
  CONVOCATION: <Badge className="bg-primary">Convocation</Badge>,
  PARTY: <Badge className="bg-violet-600">Party</Badge>,
};

export default function CheckinTransactions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [checkinType, setCheckinType] = useState(ANY);
  const [qrType, setQrType] = useState(ANY);
  const [entity, setEntity] = useState("");
  const [program, setProgram] = useState("");
  const [specialization, setSpecialization] = useState("");

  useEffect(() => {
    const id = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(id);
  }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/checkin/transactions", {
        params: {
          q: debouncedQ,
          checkin_type: checkinType === ANY ? "" : checkinType,
          qr_type: qrType === ANY ? "" : qrType,
          entity, program, specialization,
          page, page_size: PAGE_SIZE,
        },
      });
      setRows(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [debouncedQ, checkinType, qrType, entity, program, specialization, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setQ(""); setCheckinType(ANY); setQrType(ANY); setEntity(""); setProgram(""); setSpecialization("");
  };
  const filtersActive = q || checkinType !== ANY || qrType !== ANY || entity || program || specialization;

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="checkin-transactions-page">
      <PageHeader
        title="Check-in Transactions"
        subtitle="Every convocation and party check-in recorded, with filters."
      />

      <div className="bg-white border border-border rounded-xl">
        <div className="px-6 py-4 border-b border-border space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="tx-search"
                className="pl-8" placeholder="Search by roll no., name, or guest…" value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select value={checkinType} onValueChange={(v) => { setCheckinType(v); setPage(1); }}>
              <SelectTrigger className="w-44" data-testid="tx-filter-checkin-type"><SelectValue placeholder="Check-in Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any Check-in Type</SelectItem>
                <SelectItem value="CONVOCATION">Convocation</SelectItem>
                <SelectItem value="PARTY">Party</SelectItem>
              </SelectContent>
            </Select>
            <Select value={qrType} onValueChange={(v) => { setQrType(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="tx-filter-qr-type"><SelectValue placeholder="QR Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Student or Guest</SelectItem>
                <SelectItem value="STUDENT">Student</SelectItem>
                <SelectItem value="GUEST">Guest</SelectItem>
              </SelectContent>
            </Select>
            {filtersActive && (
              <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="tx-clear-filters">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              data-testid="tx-filter-entity"
              className="w-56" placeholder="Filter by entity…" value={entity}
              onChange={(e) => { setEntity(e.target.value); setPage(1); }}
            />
            <Input
              data-testid="tx-filter-program"
              className="w-56" placeholder="Filter by program…" value={program}
              onChange={(e) => { setProgram(e.target.value); setPage(1); }}
            />
            <Input
              data-testid="tx-filter-specialization"
              className="w-56" placeholder="Filter by specialization…" value={specialization}
              onChange={(e) => { setSpecialization(e.target.value); setPage(1); }}
            />
            <p className="text-sm text-slate-500 ml-auto shrink-0">
              {total} record{total === 1 ? "" : "s"} · Page {page} / {pages}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>HTNO</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Check-in</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Specialization</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r.id} data-testid={`tx-row-${r.id}`}>
                  <TableCell className="font-medium">{r.htno}</TableCell>
                  <TableCell>
                    {r.student_name}
                    {r.qr_type === "GUEST" && (
                      <span className="block text-xs text-slate-500">
                        Guest {r.guest_index}: {r.guest_name || "—"} ({r.guest_relation || "—"})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.qr_type === "GUEST" ? `Guest ${r.guest_index || ""}` : "Student"}</Badge>
                  </TableCell>
                  <TableCell>{CHECKIN_TYPE_BADGE[r.checkin_type] || r.checkin_type}</TableCell>
                  <TableCell>{r.entity}</TableCell>
                  <TableCell>{r.program}</TableCell>
                  <TableCell>{r.specialization}</TableCell>
                  <TableCell className="whitespace-nowrap">{new Date(r.checked_in_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No check-ins match these filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-t border-border">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="tx-prev">
            <ChevronLeft size={14} /> Prev
          </Button>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="tx-next">
            Next <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
